import { Box, Button, CloseButton, Group, Image, Stack, Text, ThemeIcon } from "@mantine/core";
import { visualsFor } from "./categories";
import { announcementOpenPath } from "./helpers";
import { sentAtNote } from "./time";
import type { PortalAnnouncement } from "./types";

/** Compact notification card for a single announcement.
 *
 *  Surface-coloured (adapts to light/dark) with a severity-driven accent —
 *  a left stripe + an icon chip + a coloured category label — so a *warning*
 *  reads amber and *critical* reads red, while the category emoji/label still
 *  identifies the kind. Placement (e.g. bottom-left fixed, or inside the
 *  header popover) is the caller's responsibility; this renders just the card.
 *
 *  The image, when present, is a contained hero at the top — the same treatment
 *  ``AnnouncementModal`` gives it, and for the same reason: these are designed
 *  posters, so the text chrome must not fight them. Without it an image-only
 *  announcement (no title, no message — which operators do post) rendered as
 *  nothing but its category label, saying strictly less than the poster it was
 *  built around.
 *
 *  ``onOpen`` + the announcement's ``portal_path`` add an "Open" button that
 *  hands the path to the host (the portal navigates there, e.g. to a guide). */
export function AnnouncementCorner({
  announcement,
  onDismiss,
  onOpen,
}: {
  announcement: PortalAnnouncement;
  onDismiss?: () => void;
  onOpen?: (path: string) => void;
}) {
  const visuals = visualsFor(announcement.category);
  const message = announcement.message?.trim();
  const image = announcement.image;
  // Mantine colour from the CATEGORY too, so the badge/border/icon agree
  // with the background every other surface paints (#327).
  const color = visuals.mantineColor;
  const pulse = visuals.pulseLed || announcement.severity === "critical";
  const openPath = onOpen ? announcementOpenPath(announcement) : null;
  const title = announcement.title?.trim() || visuals.label;

  return (
    <Box
      maw={360}
      pos="relative"
      style={{
        background: "var(--mantine-color-body)",
        border: "1px solid var(--mantine-color-default-border)",
        borderLeft: `4px solid var(--mantine-color-${color}-filled)`,
        borderRadius: "var(--mantine-radius-md)",
        boxShadow: "var(--mantine-shadow-lg)",
        // Clips the hero to the card's rounded corners; without it the image
        // squares off the top two.
        overflow: "hidden",
        ...(pulse ? { animation: "nodus-ann-pulse 1.6s ease-in-out infinite" } : {}),
      }}
    >
      {onDismiss && (
        <CloseButton
          aria-label="Dismiss announcement"
          onClick={onDismiss}
          size="sm"
          pos="absolute"
          style={{
            right: 6,
            top: 6,
            zIndex: 1,
            // Over a hero the button can land on any colour, so it carries its
            // own scrim rather than relying on the card surface behind it.
            ...(image?.url
              ? {
                  background: "var(--mantine-color-body)",
                  boxShadow: "0 0 0 1px var(--mantine-color-default-border)",
                }
              : {}),
          }}
        />
      )}
      {image?.url && (
        <Image
          src={image.url}
          alt={image.alt_text || ""}
          h={140}
          fit={image.object_fit === "contain" ? "contain" : "cover"}
          // ``object_position`` only bites for cover/fill, but honouring it
          // costs nothing and the operator chose it deliberately.
          style={{ objectPosition: image.object_position || "center" }}
        />
      )}
      <Group p="sm" gap="sm" wrap="nowrap" align="flex-start" pr={onDismiss ? 18 : 0}>
        <ThemeIcon color={color} variant="light" radius="xl" size="lg" style={{ flexShrink: 0, fontSize: 18 }}>
          {visuals.icon}
        </ThemeIcon>
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text fz="xs" fw={700} c={`${color}.7`} style={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
            {visuals.label}
            {announcement.is_site_wide ? " · Site-wide" : ""}
          </Text>
          <Text fw={600} lh={1.25}>
            {title}
          </Text>
          {message && (
            <Text fz="sm" c="dimmed" lh={1.35}>
              {message}
            </Text>
          )}
          {sentAtNote(announcement.raised_at) && (
            <Text fz="xs" c="dimmed" style={{ opacity: 0.8 }}>
              {sentAtNote(announcement.raised_at)}
            </Text>
          )}
          {openPath && (
            <Group mt={6}>
              <Button
                size="compact-sm"
                variant="light"
                color={color}
                onClick={(e) => {
                  // The card can sit inside a clickable row (the header bell's list).
                  e.stopPropagation();
                  onOpen?.(openPath);
                }}
                // Keys on the button are the button's, not a clickable parent's.
                onKeyDown={(e) => e.stopPropagation()}
                aria-label={`Open: ${title}`}
              >
                Open
              </Button>
            </Group>
          )}
        </Stack>
      </Group>
      <style>{`@keyframes nodus-ann-pulse{0%,100%{opacity:1}50%{opacity:.94}}`}</style>
    </Box>
  );
}
